import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Divider,
  Alert,
  Grid,
  Paper
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  Description as DescriptionIcon,
  CameraAlt as CameraIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';

interface DocumentoVerificacion {
  id: number;
  tipo_documento: string;
  archivo_url: string;
  archivo_nombre: string;
  fecha_subida: string;
}

interface Verificacion {
  id: number;
  estado: string;
  fecha_creacion: string;
  fecha_verificacion: string | null;
  onfido_result: any;
  documentos_verificacion: DocumentoVerificacion[];
}

interface Firmante {
  id: number;
  token: string;
  estado: string;
  fecha_creacion: string;
  fecha_expiracion: string;
  fecha_uso: string | null;
  agreement_id: string | null;
  s3_path: string | null;
  cliente: {
    id: number;
    nombre: string;
    email: string;
    telefono: string;
    tipo_documento: string;
    numero_documento: string;
  };
  verificaciones: Verificacion[];
}

interface FirmanteDetalleProps {
  firmante: Firmante;
  onDownloadDocument?: (url: string, filename: string) => void;
}

const FirmanteDetalle: React.FC<FirmanteDetalleProps> = ({ firmante, onDownloadDocument }) => {
  const [expanded, setExpanded] = useState<string | false>('datos-personales');

  const handleChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'VERIFICADO':
      case 'VALIDADO':
      case 'FIRMADO':
        return 'success';
      case 'FIRMANDO':
        return 'warning';
      case 'PENDIENTE':
      case 'PENDIENTE_SMS':
        return 'info';
      case 'CANCELADO':
      case 'RECHAZADO':
        return 'error';
      default:
        return 'default';
    }
  };

  const getEstadoIcon = (estado: string) => {
    switch (estado) {
      case 'VERIFICADO':
      case 'VALIDADO':
      case 'FIRMADO':
        return <CheckCircleIcon />;
      case 'CANCELADO':
      case 'RECHAZADO':
        return <ErrorIcon />;
      default:
        return <PersonIcon />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDownload = (url: string, filename: string) => {
    if (onDownloadDocument) {
      onDownloadDocument(url, filename);
    } else {
      // Fallback: abrir en nueva ventana
      window.open(url, '_blank');
    }
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h3">
            {firmante.cliente.nombre}
          </Typography>
          <Chip
            icon={getEstadoIcon(firmante.estado)}
            label={firmante.estado}
            color={getEstadoColor(firmante.estado) as any}
            variant="outlined"
          />
        </Box>

        {/* Datos Personales */}
        <Accordion expanded={expanded === 'datos-personales'} onChange={handleChange('datos-personales')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center">
              <PersonIcon sx={{ mr: 1 }} />
              <Typography>Datos Personales</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" mb={1}>
                  <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    <strong>Email:</strong> {firmante.cliente.email}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" mb={1}>
                  <PhoneIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    <strong>Teléfono:</strong> {firmante.cliente.telefono}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" mb={1}>
                  <BadgeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  <Typography variant="body2">
                    <strong>Documento:</strong> {firmante.cliente.tipo_documento} {firmante.cliente.numero_documento}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Información del Link */}
        <Accordion expanded={expanded === 'info-link'} onChange={handleChange('info-link')}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center">
              <DescriptionIcon sx={{ mr: 1 }} />
              <Typography>Información del Link</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" mb={1}>
                  <strong>Token:</strong> {firmante.token}
                </Typography>
                <Typography variant="body2" mb={1}>
                  <strong>Agreement ID:</strong> {firmante.agreement_id || 'No asignado'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" mb={1}>
                  <strong>Creado:</strong> {formatDate(firmante.fecha_creacion)}
                </Typography>
                <Typography variant="body2" mb={1}>
                  <strong>Expira:</strong> {formatDate(firmante.fecha_expiracion)}
                </Typography>
                {firmante.fecha_uso && (
                  <Typography variant="body2" mb={1}>
                    <strong>Usado:</strong> {formatDate(firmante.fecha_uso)}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Verificaciones */}
        {firmante.verificaciones && firmante.verificaciones.length > 0 && (
          <Accordion expanded={expanded === 'verificaciones'} onChange={handleChange('verificaciones')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center">
                <CheckCircleIcon sx={{ mr: 1 }} />
                <Typography>Verificaciones ({firmante.verificaciones.length})</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {firmante.verificaciones.map((verificacion, index) => (
                <Paper key={verificacion.id} sx={{ p: 2, mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle2">
                      Verificación #{index + 1}
                    </Typography>
                    <Chip
                      label={verificacion.estado}
                      color={getEstadoColor(verificacion.estado) as any}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" mb={1}>
                    <strong>Creada:</strong> {formatDate(verificacion.fecha_creacion)}
                  </Typography>
                  
                  {verificacion.fecha_verificacion && (
                    <Typography variant="body2" mb={2}>
                      <strong>Verificada:</strong> {formatDate(verificacion.fecha_verificacion)}
                    </Typography>
                  )}

                  {/* Documentos de Verificación */}
                  {verificacion.documentos_verificacion && verificacion.documentos_verificacion.length > 0 && (
                    <Box>
                      <Typography variant="subtitle2" mb={1}>
                        Documentos Subidos:
                      </Typography>
                      <List dense>
                        {verificacion.documentos_verificacion.map((doc) => (
                          <ListItem key={doc.id} sx={{ pl: 0 }}>
                            <ListItemIcon>
                              {doc.tipo_documento === 'SELFIE' ? <CameraIcon /> : <DescriptionIcon />}
                            </ListItemIcon>
                            <ListItemText
                              primary={doc.tipo_documento}
                              secondary={`${doc.archivo_nombre} - ${formatDate(doc.fecha_subida)}`}
                            />
                            <Button
                              size="small"
                              startIcon={<VisibilityIcon />}
                              onClick={() => handleDownload(doc.archivo_url, doc.archivo_nombre)}
                            >
                              Ver
                            </Button>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Resultado de Onfido */}
                  {verificacion.onfido_result && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" mb={1}>
                        Resultado Onfido:
                      </Typography>
                      <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                        <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(verificacion.onfido_result, null, 2)}
                        </pre>
                      </Alert>
                    </Box>
                  )}
                </Paper>
              ))}
            </AccordionDetails>
          </Accordion>
        )}

      </CardContent>
    </Card>
  );
};

export default FirmanteDetalle;
